// @ts-nocheck: converted from JS

import { approvalStatusToChecklistStatusMap } from '@/components/Studies/Approval/Approval.utils';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import UserStatusAvatar from '@/components/UI/UserStatusAvatar';
import { fetchMetadataColumns, fetchStudiesMetadataColumns } from '@/helpers';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError.ts';
import { statusToTextMap } from '@/referenceData/study/status';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { web as webRoute } from '@/support/route';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

export const getApprovers = (approval, team) => {
  const getApproversByIds = (approverIds) =>
    approverIds.map((approverId) => team.users.find((user) => user.id === approverId));
  return getApproversByIds(approval?.approval?.approvers?.ids ?? []).reduce((acc, approver) => {
    if (_notNil(approver)) {
      const actionedApprover = approval.approvers.find((assignedApprover) => assignedApprover.id === approver.id);
      acc.push({
        name: approver.name,
        email: approver.email,
        status: getApproverStatus(approver, actionedApprover),
      });
    }
    return acc;
  }, []);
};

const getAdditionalApprovers = (approval, team) => {
  const getApproversByIds = (approverIds) =>
    approverIds.map((approverId) => team.users.find((user) => user.id === approverId));
  return _notNil(approval.approval.approvers.required_ids) && _notNil(approval.required_approvers)
    ? getApproversByIds(approval.approval.approvers.required_ids).map((approver) => {
        const actionedApprover = approval.required_approvers?.find(
          (assignedApprover) => assignedApprover.id === approver.id
        );
        return {
          name: approver.name,
          email: approver.email,
          status: getApproverStatus(approver, actionedApprover),
        };
      })
    : [];
};

const getApproverStatus = (approver, actionedApprover) => {
  if (approver.pivot?.status !== 'inactive') {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    return approvalStatusToChecklistStatusMap[actionedApprover?.response] || 'idle';
  } else if (actionedApprover?.response === 'approved') {
    return 'inactiveSuccess';
  } else {
    return 'inactive';
  }
};

/**
 *
 * @param props {{study: Study,studyLink?: StudyLink}}
 * @returns {JSX.Element|string}
 * @constructor
 */
const StudyNameCell = ({ study, studyLink }) => {
  if (_notNil(studyLink?.access)) {
    return (
      <Link
        to={webRoute('studies.show', {
          id: study.id,
        })}
        openTab
        className="link blue"
      >
        {study.name}
      </Link>
    );
  }
  return study?.name ?? '';
};

export const generateColumns = async (team, signal: AbortSignal) => {
  let metadata = [];
  try {
    metadata = await fetchStudiesMetadataColumns(signal);
  } catch (error) {
    if (notAborted(error)) {
      ExceptionHandler.captureException(
        new InVivoError('Could not fetch studies metadata columns', {
          cause: error,
          slug: 'approval-study-metadata',
        })
      );
    }
  }

  return [
    {
      id: 'name',
      header: 'Review name',
      accessorKey: 'approval.name',
      isVisible: true,
      enableSorting: true,
      cell: ({ row }) => (
        <Link
          to={webRoute('approvals.details', {
            approvalRequestId: row.original.id,
          })}
          className="link blue"
        >
          {row.original.approval.name}
        </Link>
      ),
    },
    {
      id: 'studyName',
      header: 'Study Name',
      accessorKey: 'study.name',
      isVisible: true,
      enableSorting: false,
      cell: ({ row }) => <StudyNameCell study={row.original.study} studyLink={row.original.study_link} />,
    },
    {
      id: 'studyCreator',
      header: 'Study Creator',
      accessorKey: 'study.author.name',
      enableSorting: false,
      isVisible: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      isVisible: true,
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      cell: ({ row }) => statusToTextMap[row.original.status] || '',
    },
    {
      id: 'reviewers',
      header: 'Reviewers',
      isVisible: true,
      enableSorting: false,
      cell: ({ row }) => {
        const approvers = [...getApprovers(row.original, team), ...getAdditionalApprovers(row.original, team)];
        return (
          <div className="flex flex-row">
            {approvers.map((approver) => (
              <UserStatusAvatar className="mr1" key={approver.email} user={approver} />
            ))}
          </div>
        );
      },
    },
    {
      id: 'time',
      header: 'Submitted At',
      isVisible: true,
      accessorKey: 'submitted_at',
      enableSorting: true,
      cell: ({ row }) => <DateTimeRenderer value={row.original.submitted_at} defaultResponse="-" />,
    },
    ...fetchMetadataColumns({ metadata, nestedStudyLocation: 'study' }),
  ];
};

export const searchByText = 'Search by review name';
